import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Checkout } from "shared/models";
import { useStoreAPI } from "shared/services/api";

import { useAuthContext } from "~/features/auth";

export const useCheckout = (id: string) => {
  const { token } = useAuthContext();
  const api = useStoreAPI({ accessToken: token });
  const queryClient = useQueryClient();
  const [checkout, setCheckout] = useState<Checkout>(new Checkout());

  const { isLoading, refetch } = useQuery(
    ["checkout", id],
    () => api.getCheckout(id),
    {
      onSuccess: ({ data: { checkout } }) => {
        setCheckout(new Checkout(checkout));
      },
    },
  );

  const { mutateAsync: cancelMutate, isLoading: isCancelFetching } = useMutation(
    (params?: { restoreToCart?: boolean }) => api.cancelCheckout(id, { restoreToCart: params?.restoreToCart }),
    {
      onSuccess: async (_, params) => {
        await refetch();
        if (params?.restoreToCart) {
          await queryClient.invalidateQueries({ queryKey: ["carts"] });
        }
      },
    },
  );

  const { mutateAsync: updatePaymentMethod, isLoading: isUpdatePaymentMethodFetching } = useMutation(
    (params: { paymentMethodId: string }) => api.updateCheckoutPaymentMethod(id, params),
    {
      onSuccess: () => {
        refetch();
      },
    },
  );

  return {
    checkout,
    cancelMutate,
    updatePaymentMethod,
    isLoading,
    isCancelFetching,
    isUpdatePaymentMethodFetching,
    isFetching: isCancelFetching || isUpdatePaymentMethodFetching,
  };
};
